import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { availabilityTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { Company, FavoriteSitterService, PagedResponseData, ParentSearchBar, Sitter, SitterService, UserGeocode } from 'us-web-services';
import { useDependencies } from '../../../../../dependencies';
import { GroupSitters } from './GroupSitters';

interface Props {
  companies: Company[];
  newSearch: ParentSearchBar;
  geocode: UserGeocode;
  getWillSitParams(search: ParentSearchBar): object;
}

type CompanySitter = {
  company: Company;
  sitters: PagedResponseData<Sitter>;
  params: object;
}

export const CompanyGroups = observer((props: Props): JSX.Element => {
  const { stores } = useDependencies();
  const [companySittersList, setCompanySittersList] = useState<CompanySitter[]>([]);

  const { user } = stores.user;
  const { parent } = stores.parent;
  const { companies, geocode, newSearch, getWillSitParams } = props;

  const setFavorite = async (isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void> => {
    const config = {
      parentId: user.id,
      sitterId,
    };

    if (isFavorited) {
      await FavoriteSitterService.create(config);
    } else {
      await FavoriteSitterService.delete(favoriteId);
    }
  }

  const getCompanySitters = async (params: object): Promise<void> => {
    const companySitters: CompanySitter[] = [];

    await Promise.allSettled(companies.map(async (company) => {
      if (company.alias) {
        const sitterParams = {
          companyId: company.id,
          ...params,
        };
        const config = {
          params: sitterParams,
        };
        const response = (await SitterService.getByFilter(config)).data;

        companySitters.push({
          company,
          sitters: response,
          params: sitterParams,
        });
      }
    }));

    setCompanySittersList(companySitters);
  }

  useEffect(() => {
    if (!geocode.zip) {
      return;
    }

    const originalParams = {
      limit: 4,
      userId: user.id,
      parentId: user.id,
      city: geocode?.city ?? parent?.address?.city,
      zip: newSearch.zip ?? parent?.address?.zip,
      state: geocode?.state ?? parent?.address?.state,
      distance: newSearch.distance ?? 25,
      latitude: geocode?.latitude ?? parent?.address?.latitude,
      longitude: geocode?.longitude ?? parent?.address?.longitude,
      numberOfChildren: parent?.kids?.length || 1,
      verticalTypeId: newSearch.vertical?.id ?? bookingVerticalType.CHILD_CARE,
      availabilityType: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.type ?? 'All',
      searchTypeCode: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.code ?? 'ALL',
      excludeFavorites: true,
    };

    const additionalParams = getWillSitParams(newSearch);
    const params = {
      ...originalParams,
      ...additionalParams,
    };

    getCompanySitters(params);
  }, [geocode, newSearch]);

  return (
    <div className='bg-transparent'>
      {companySittersList.map(companySitters => (companySitters.sitters.meta.size > 0 && (
        <div className='w-full' key={`company-${companySitters.company.id}`}>
          <GroupSitters sitters={companySitters.sitters} company={companySitters.company} params={companySitters.params} hideExperience={false} setFavorite={setFavorite}/>
        </div>
      )))}
    </div>
  );
});
